import './ButtonWithChat.styles.css';

import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import React, { FC, useCallback, useEffect, useMemo } from 'react';

import chatStore from '../../../../store/ChatStore';
import gameStore from '../../../../store/GameStore';
import tableStore from '../../../../store/TableStore';
import userSettingsStore from '../../../../store/UserSettingsStore';
import Chat from '../../../Chat/Chat';
import { ButtonWithChatProps } from './ButtonWithChat.types';

const ButtonWithChat: FC<ButtonWithChatProps> = ({
  tableId,
  expertMode = false,
  emoji = false,
}) => {
  useEffect(() => {
    Array.from(chatStore.toggleChatEventMap.keys()).forEach(tableId => {
      if (!tableStore.isTableViewing(tableId)) {
        chatStore.removeToggleEvent(tableId);
      }
    });
  }, [tableId, gameStore.activeGames.size, tableStore.viewingTableIds.length]);

  const getToggleChatEvent = useCallback(
    (tableId: string) => chatStore.toggleChatEventMap.get(tableId),
    [tableId, chatStore.toggleChatEventMap.size],
  );

  const unreadMessageCount = useMemo(
    () => chatStore.getUnreadMessageNumber(tableId),
    [tableId, chatStore.chatStateChanged],
  );

  const unreadChatMessagesBadge = useMemo(() => {
    let unreadMessageNumber: number;
    if (!expertMode) {
      unreadMessageNumber = unreadMessageCount;
    } else {
      unreadMessageNumber = emoji
        ? chatStore.getUnreadEmojiMessagesCount(tableId)
        : chatStore.getUnreadTextMessagesCount(tableId);
    }
    return unreadMessageNumber > 0 ? unreadMessageNumber.toString() : undefined;
  }, [unreadMessageCount, emoji, expertMode]);

  const table = useMemo(
    () => tableStore.getActiveTableById(tableId),
    [tableId, tableStore.activeTables.size],
  );

  return (
    table && (
      <>
        <Button
          id={`chat-btn-${tableId}`}
          className={unreadMessageCount > 9 ? 'expandable-button' : ''}
          icon={`pi ${emoji ? 'pi-face-smile' : 'pi-comment'}`}
          badge={unreadChatMessagesBadge}
          badgeClassName="p-badge-danger m-0"
          disabled={userSettingsStore.settings.chat.disabled}
          tooltipOptions={{ showOnDisabled: true }}
          tooltip={
            userSettingsStore.settings.chat.disabled
              ? 'Chat is disabled in settings'
              : 'Chat'
          }
          rounded
          text
          severity="info"
          aria-label="chat"
          onClick={event => chatStore.addToggleChatEventMap(tableId, event)}
        />
        {!userSettingsStore.settings.chat.disabled && (
          <Chat table={table} toggleEvent={getToggleChatEvent(tableId)} />
        )}
      </>
    )
  );
};

export default observer(ButtonWithChat);
